"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "left" | "right" | "none";
  effect?: "fade" | "reveal" | "scale" | "blur";
}

export function AnimatedSection({
  children,
  className,
  delay = 0,
  direction = "up",
  effect = "fade",
}: AnimatedSectionProps) {
  const shouldReduceMotion = usePrefersReducedMotion();

  if (effect === "reveal") {
    const clipMap = {
      up: "inset(100% 0 0 0)",
      left: "inset(0 100% 0 0)",
      right: "inset(0 0 0 100%)",
      none: "inset(100% 0 0 0)",
    };

    return (
      <motion.div
        className={cn(className)}
        initial={
          shouldReduceMotion ? false : { clipPath: clipMap[direction] }
        }
        whileInView={{ clipPath: "inset(0 0 0 0)" }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.9,
          ease: [0.25, 0.46, 0.45, 0.94],
          delay: shouldReduceMotion ? 0 : delay,
        }}
      >
        {children}
      </motion.div>
    );
  }

  if (effect === "scale") {
    return (
      <motion.div
        className={cn(className)}
        initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.92 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.7,
          ease: "easeOut",
          delay: shouldReduceMotion ? 0 : delay,
        }}
      >
        {children}
      </motion.div>
    );
  }

  if (effect === "blur") {
    return (
      <motion.div
        className={cn(className)}
        initial={
          shouldReduceMotion
            ? false
            : { opacity: 0, filter: "blur(8px)", y: 15 }
        }
        whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.8,
          ease: "easeOut",
          delay: shouldReduceMotion ? 0 : delay,
        }}
      >
        {children}
      </motion.div>
    );
  }

  const initialOffset = {
    up: { x: 0, y: 30 },
    left: { x: -30, y: 0 },
    right: { x: 30, y: 0 },
    none: { x: 0, y: 0 },
  };

  const { x, y } = initialOffset[direction];

  return (
    <motion.div
      className={cn(className)}
      initial={shouldReduceMotion ? false : { opacity: 0, x, y }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{
        duration: shouldReduceMotion ? 0 : 0.6,
        ease: "easeOut",
        delay: shouldReduceMotion ? 0 : delay,
      }}
    >
      {children}
    </motion.div>
  );
}
